import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { LibraryItem } from "@/api/models/api-types";
import {
  ALL_ACCEPTED_TYPES,
  DEFAULT_MAX_FILE_SIZE,
  formatFileSize,
  generateFileTitle,
  getFileCategory,
  isR2Url,
  uploadFile,
  uploadFromUrl,
  type UploadFileResponse,
} from "@/features/file-upload";
import { useToast } from "@/features/toast";
import { getFileUrl } from "@/lib/utils";
import { isLocalFileAccepted, isMimeAccepted } from "../lib/pick-compatibility";
import { useFilePickerStore } from "../store/file-picker-store";
import type { PickerConfig, SelectedFile } from "../types/file-picker-types";

const DEFAULT_MAX_SELECTION = 10;

const URL_EXTENSION_MIME: Record<string, string> = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  svg: "image/svg+xml",
  avif: "image/avif",
  mp4: "video/mp4",
  webm: "video/webm",
  mov: "video/quicktime",
  mp3: "audio/mpeg",
  wav: "audio/wav",
  oga: "audio/ogg",
  ogg: "audio/ogg",
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
};

// Yükleme sürerken seçici kapanıp yeniden açılırsa sonuç yeni oturuma düşmemeli
const isSameSession = (config: PickerConfig | null) => {
  const state = useFilePickerStore.getState();
  return state.isOpen && state.config === config;
};

const getFreeSlots = () => {
  const state = useFilePickerStore.getState();
  const max = state.config?.maxSelection || DEFAULT_MAX_SELECTION;
  const inFlight = state.uploads.filter((upload) => upload.status === "uploading").length;
  return max - state.selectedFiles.length - inFlight;
};

const createUploadId = () => `upload-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const toSelectedUpload = (response: UploadFileResponse, fallbackName: string): SelectedFile => {
  const data = response.data;
  return {
    id: data._id,
    name: data.title || fallbackName,
    size: data.file.size,
    type: data.file.type,
    source: "local",
    url: data.file.fileUrl,
    thumbnail: data.type === "image" ? getFileUrl(data.file) || undefined : undefined,
    createdAt: data.createdAt,
  };
};

/**
 * Seçicinin tüm yükleme yolları (tıkla, bırak, yapıştır, URL) buradan geçer.
 * Yüklenen dosya Library'ye girer ve seçime eklenir; kuyruk store'da tutulur ki
 * ızgara ve yükleme paneli aynı ilerlemeyi göstersin.
 */
export const usePickerUpload = () => {
  const { t } = useTranslation();
  const { error: toastError } = useToast();
  const queryClient = useQueryClient();

  const finishUpload = useCallback(
    (uploadId: string, response: UploadFileResponse, fallbackName: string, config: PickerConfig | null) => {
      queryClient.invalidateQueries({ queryKey: ["library"] });
      queryClient.invalidateQueries({ queryKey: ["library-stats"] });
      if (!isSameSession(config)) return;
      const state = useFilePickerStore.getState();
      state.removeUpload(uploadId);
      state.selectFile(
        toSelectedUpload(response, fallbackName),
        response.data as unknown as LibraryItem
      );
    },
    [queryClient]
  );

  const uploadFiles = useCallback(
    async (input: FileList | File[]) => {
      const { config, addUpload, updateUpload } = useFilePickerStore.getState();
      const files = Array.from(input);
      if (files.length === 0) return;

      const accepted = files.filter(
        (file) =>
          isLocalFileAccepted(file, config) &&
          (!file.type || (ALL_ACCEPTED_TYPES as readonly string[]).includes(file.type))
      );
      if (accepted.length < files.length) {
        toastError(t("filePicker.invalidFileType"));
      }

      const tooLarge = accepted.filter((file) => file.size > DEFAULT_MAX_FILE_SIZE);
      if (tooLarge.length > 0) {
        toastError(t("filePicker.fileTooLarge", { max: formatFileSize(DEFAULT_MAX_FILE_SIZE) }));
      }
      const valid = accepted.filter((file) => file.size <= DEFAULT_MAX_FILE_SIZE);
      if (valid.length === 0) return;

      const slots = getFreeSlots();
      if (valid.length > slots) {
        toastError(
          t("filePicker.limitReached", { count: config?.maxSelection || DEFAULT_MAX_SELECTION })
        );
      }
      const batch = valid.slice(0, Math.max(slots, 0));

      await Promise.all(
        batch.map(async (file) => {
          const uploadId = createUploadId();
          addUpload({
            id: uploadId,
            name: file.name,
            mimeType: file.type,
            previewUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : undefined,
            status: "uploading",
          });
          try {
            const response = await uploadFile({
              file,
              title: generateFileTitle(file),
              isInput: true,
              isWardrobe: config?.isWardrobe,
              tags: config?.uploadTags,
            });
            finishUpload(uploadId, response, file.name, config);
          } catch {
            if (isSameSession(config)) {
              updateUpload(uploadId, { status: "error", error: t("filePicker.uploadError") });
            }
          }
        })
      );
    },
    [finishUpload, t, toastError]
  );

  const uploadUrl = useCallback(
    async (rawUrl: string) => {
      const url = rawUrl.trim();
      const { config, addUpload, updateUpload, removeUpload, selectFile } =
        useFilePickerStore.getState();
      try {
        new URL(url);
      } catch {
        toastError(t("filePicker.invalidUrl"));
        return false;
      }
      if (getFreeSlots() <= 0) {
        toastError(
          t("filePicker.limitReached", { count: config?.maxSelection || DEFAULT_MAX_SELECTION })
        );
        return false;
      }

      // Kendi depomuzdaki dosya yeniden yüklenmez
      if (isR2Url(url)) {
        selectFile({
          id: `url-${Date.now()}`,
          name: url.split("/").pop() || "URL",
          size: 0,
          type: "url",
          source: "local",
          url,
        });
        return true;
      }

      const extension = new URL(url).pathname.split(".").pop()?.toLowerCase() ?? "";
      const guessedMime = URL_EXTENSION_MIME[extension];
      if (guessedMime && !isMimeAccepted(guessedMime, config)) {
        toastError(t("filePicker.invalidFileType"));
        return false;
      }
      const category = getFileCategory(guessedMime ?? "image/jpeg");

      const uploadId = createUploadId();
      addUpload({
        id: uploadId,
        name: url.split("/").pop() || url,
        mimeType: guessedMime ?? "",
        status: "uploading",
      });
      try {
        const response = await uploadFromUrl({
          url,
          type: category === "unknown" ? "image" : category,
          isInput: true,
          isWardrobe: config?.isWardrobe,
          tags: config?.uploadTags,
        });
        if (!isMimeAccepted(response.data.file.type, config)) {
          removeUpload(uploadId);
          toastError(t("filePicker.invalidFileType"));
          return false;
        }
        finishUpload(uploadId, response, url.split("/").pop() || "URL", config);
        return true;
      } catch {
        if (isSameSession(config)) {
          updateUpload(uploadId, { status: "error", error: t("filePicker.uploadError") });
        }
        return false;
      }
    },
    [finishUpload, t, toastError]
  );

  return { uploadFiles, uploadUrl };
};
